
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, PieChart, BarChart3, TrendingUp } from 'lucide-react';
import { supabase } from '@/lib/supabaseClient';
import { toast } from 'sonner';
import { PieChart as RechartsPieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, TooltipProps } from 'recharts';
import { companyService } from '@/services/companyService';
import { useLanguage } from '@/contexts/LanguageContext';
import { ROUTES } from '@/lib/routes';
import type { CategoryKey } from '@/lib/navigation';
import { usePageFocus } from '@/hooks/usePageFocus';

const ANALYTICS_CATEGORY: CategoryKey = 'analytics';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface StatisticsData {
  totalProducts: number;
  totalValue: number;
  totalInventory: number;
  categoryStats: Array<{ name: string; value: number; products: number }>;
  brandStats: Array<{ name: string; value: number; products: number }>;
  statusStats: Array<{ name: string; value: number; products: number }>;
  warehouseStats: Array<{ name: string; value: number; products: number }>;
}

const ProductStatistics = () => {
  const [statistics, setStatistics] = useState<StatisticsData>({
    totalProducts: 0,
    totalValue: 0,
    totalInventory: 0,
    categoryStats: [],
    brandStats: [],
    statusStats: [],
    warehouseStats: []
  });
  const [loading, setLoading] = useState(false);
  const [chartType, setChartType] = useState<'brand'>('brand'); // 默认改为品牌
  const [currentCompany, setCurrentCompany] = useState<{ id: string; code?: string; name?: string } | null>(null);
  const navigate = useNavigate();
  const { language } = useLanguage();

  // 跟踪加载开始时间，用于检测stale loading状态
  const loadingStartedAtRef = useRef<number | null>(null);

  // 跟踪stale loading恢复定时器
  const staleRecoveryTimerRef = useRef<number | null>(null);

  useEffect(() => {
    const initializeCompany = async () => {
      try {
        const company = await companyService.getCurrentCompany();
        if (company) {
          setCurrentCompany({ id: String(company.id), code: company.code, name: company.name });
          fetchStatistics(company.id);
        }
      } catch (error) {
        console.error('Failed to get company:', error);
        toast.error(language === 'zh' ? '獲取公司資訊失敗' : 'Failed to get company information');
      }
    };

    initializeCompany();
  }, []);

  // 页面焦点恢复处理器
  const handlePageFocusRecovery = useCallback(async () => {
    const now = Date.now();
    const STALE_LOADING_THRESHOLD = 3000; // 3秒

    // 检查是否是stale loading状态
    if (loading && loadingStartedAtRef.current) {
      const loadingDuration = now - loadingStartedAtRef.current;
      if (loadingDuration > STALE_LOADING_THRESHOLD) {
        console.log(`🔄 檢測到stale loading狀態 (${loadingDuration}ms)，重置loading狀態`);
        setLoading(false);
        loadingStartedAtRef.current = null;
      } else {
        // 仍在阈值内，安排定时器在剩余时间内触发恢复
        const remainingTime = STALE_LOADING_THRESHOLD - loadingDuration;
        console.log(`🔄 產品統計頁面正在加載中 (${loadingDuration}ms)，安排${remainingTime}ms後自動恢復`);

        // 清除任何现有的定时器
        if (staleRecoveryTimerRef.current) {
          clearTimeout(staleRecoveryTimerRef.current);
        }

        // 安排自动恢复
        staleRecoveryTimerRef.current = window.setTimeout(async () => {
          console.log('🔄 自動恢復：重置loading狀態並重新加載數據');
          setLoading(false);
          loadingStartedAtRef.current = null;
          staleRecoveryTimerRef.current = null;

          try {
            if (currentCompany) {
              await fetchStatistics(parseInt(currentCompany.id));
            } else {
              const company = await companyService.getCurrentCompany();
              if (company) {
                setCurrentCompany({ id: String(company.id), code: company.code, name: company.name });
                await fetchStatistics(company.id);
              }
            }
          } catch (error) {
            console.error('產品統計頁面自動恢復失敗:', error);
          }
        }, remainingTime);

        return;
      }
    } else if (loading) {
      console.log('🔄 產品統計頁面正在加載中，跳過焦點恢復');
      return;
    }

    try {
      if (currentCompany) {
        console.log('🔄 產品統計頁面焦點恢復，重新加載數據');
        await fetchStatistics(parseInt(currentCompany.id));
      } else {
        // 如果没有当前公司，重新初始化
        const company = await companyService.getCurrentCompany();
        if (company) {
          setCurrentCompany({ id: String(company.id), code: company.code, name: company.name });
          await fetchStatistics(company.id);
        }
      }
    } catch (error) {
      console.error('產品統計頁面焦點恢復失敗:', error);
    }
  }, [loading, currentCompany]);

  // 使用页面焦点恢复 hook
  // 禁用自動頁面焦點恢復，由用戶手動控制
  const { triggerFocusRecovery } = usePageFocus({
    onFocus: handlePageFocusRecovery,
    debounceMs: 1000,
    enabled: false
  });

  // 組件卸載時清理狀態
  useEffect(() => {
    return () => {
      // 清理統計相關狀態
      setStatistics({
        totalProducts: 0,
        totalValue: 0,
        totalInventory: 0,
        activeProducts: 0,
        inactiveProducts: 0,
        onlineProducts: 0,
        offlineProducts: 0,
        warehouseStats: [],
        brandStats: [],
        categoryStats: []
      });
      setLoading(false);
    };
  }, []);

  const fetchStatistics = async (companyId?: number) => {
    if (!companyId) return;

    setLoading(true);
    loadingStartedAtRef.current = Date.now(); // 记录加载开始时间
    try {
      const companyIdNum = Number(companyId);

      // 1. 获取当前公司的产品数据，包含 company_sku_key
      const { data: products, error: productsError } = await supabase
        .from('products')
        .select('sku_id, brand, category_code, sku_status, warehouse, company_sku_key')
        .eq('company_id', companyIdNum);

      if (productsError) throw productsError;

      // 2. 获取库存数据，包含 company_sku_key
      const { data: inventory, error: inventoryError } = await supabase
        .from('product_inventory')
        .select('sku_id, merchant_inventory_qty, company_sku_key')
        .eq('company_id', companyIdNum);

      if (inventoryError) throw inventoryError;

      // 3. 透過 RPC 在資料庫端直接計算總庫存價值 SUM(qty * cost)
      //    前端只接收 1 個數字，避免傳輸大量成本明細及 Supabase JS 1000 筆上限問題
      const { data: inventoryValueRpc, error: inventoryValueError } = await supabase
        .rpc('get_inventory_value', { p_company_id: companyIdNum });

      if (inventoryValueError) {
        console.warn('庫存價值 RPC 查詢失敗:', inventoryValueError);
      }

      // 4. 获取最新平均成本（用於品牌/分類/倉庫等 breakdown 統計）
      const { data: costData, error: costError } = await supabase
        .from('product_costs_latest')
        .select('company_sku_key, average_cost')
        .eq('company_id', companyIdNum)
        .limit(10000);

      if (costError) {
        console.warn('成本数据获取失败，breakdown 統計將以 0 代替缺失成本:', costError);
      }

      // 5. 建立 company_sku_key -> average_cost 的映射（供 breakdown 統計使用）
      const latestCosts = new Map<string, number>();
      costData?.forEach(costItem => {
        if (costItem.company_sku_key && costItem.average_cost) {
          latestCosts.set(costItem.company_sku_key, costItem.average_cost);
        }
      });

      // 6. 计算基础统计
      const totalProducts = products?.length || 0;
      const totalInventory = inventory?.reduce((sum, item) => sum + (item.merchant_inventory_qty || 0), 0) || 0;

      // 總庫存價值：直接使用 RPC 回傳的精確結果
      const totalValue = Number(inventoryValueRpc) || 0;

      // 7. 品牌统计 (使用成本计算)
      const brandMap = new Map<string, { products: number; inventory: number; value: number; uniqueSkus: Set<string> }>();
      products?.forEach(product => {
        const brand = product.brand || 'Unknown';
        if (!brandMap.has(brand)) {
          brandMap.set(brand, { products: 0, inventory: 0, value: 0, uniqueSkus: new Set() });
        }

        const inventoryItem = inventory?.find(inv => inv.company_sku_key === product.company_sku_key);
        const qty = inventoryItem?.merchant_inventory_qty || 0;
        const cost = product.company_sku_key ? latestCosts.get(product.company_sku_key) || 0 : 0;

        brandMap.get(brand)!.uniqueSkus.add(product.sku_id);
        brandMap.get(brand)!.inventory += qty;
        brandMap.get(brand)!.value += qty * cost;
      });

      // 计算每个品牌的唯一产品款式数量
      brandMap.forEach((data, brand) => {
        data.products = data.uniqueSkus.size;
      });

      const brandStats = Array.from(brandMap.entries())
        .map(([name, data]) => ({
          name,
          value: data.value,
          products: data.products,
          inventory: data.inventory
        }))
        .sort((a, b) => b.value - a.value); // 按价值降序排序

      // 8. 状态统计 (使用成本计算)
      const statusMap = new Map();
      products?.forEach(product => {
        const status = product.sku_status || 'Unknown';
        if (!statusMap.has(status)) {
          statusMap.set(status, { products: 0, value: 0 });
        }
        const inventoryItem = inventory?.find(inv => inv.company_sku_key === product.company_sku_key);
        const qty = inventoryItem?.merchant_inventory_qty || 0;
        const cost = product.company_sku_key ? latestCosts.get(product.company_sku_key) || 0 : 0;
        statusMap.get(status).products += 1;
        statusMap.get(status).value += qty * cost;
      });

      const statusStats = Array.from(statusMap.entries()).map(([name, data]) => ({
        name,
        value: data.value,
        products: data.products
      }));

      // 9. 仓库统计 (使用成本计算)
      const warehouseMap = new Map();
      products?.forEach(product => {
        const warehouse = product.warehouse || 'Unknown';
        if (!warehouseMap.has(warehouse)) {
          warehouseMap.set(warehouse, { products: 0, value: 0 });
        }
        const inventoryItem = inventory?.find(inv => inv.company_sku_key === product.company_sku_key);
        const qty = inventoryItem?.merchant_inventory_qty || 0;
        const cost = product.company_sku_key ? latestCosts.get(product.company_sku_key) || 0 : 0;
        warehouseMap.get(warehouse).products += 1;
        warehouseMap.get(warehouse).value += qty * cost;
      });

      const warehouseStats = Array.from(warehouseMap.entries()).map(([name, data]) => ({
        name,
        value: data.value,
        products: data.products
      }));

      // 10. 分类统计 (使用成本计算)
      const categoryMap = new Map();
      products?.forEach(product => {
        const category = product.category_code || 'Unknown';
        if (!categoryMap.has(category)) {
          categoryMap.set(category, { products: 0, value: 0 });
        }
        const inventoryItem = inventory?.find(inv => inv.company_sku_key === product.company_sku_key);
        const qty = inventoryItem?.merchant_inventory_qty || 0;
        const cost = product.company_sku_key ? latestCosts.get(product.company_sku_key) || 0 : 0;
        categoryMap.get(category).products += 1;
        categoryMap.get(category).value += qty * cost;
      });

      const categoryStats = Array.from(categoryMap.entries()).map(([name, data]) => ({
        name,
        value: data.value,
        products: data.products
      }));

      setStatistics({
        totalProducts,
        totalValue,
        totalInventory,
        categoryStats,
        brandStats,
        statusStats,
        warehouseStats
      });

    } catch (error: any) {
      console.error('獲取統計資料失敗:', error);
      toast.error('獲取統計資料失敗: ' + error.message);
    } finally {
      setLoading(false);
      loadingStartedAtRef.current = null; // 清除加载开始时间

      // 清除stale recovery定时器
      if (staleRecoveryTimerRef.current) {
        clearTimeout(staleRecoveryTimerRef.current);
        staleRecoveryTimerRef.current = null;
      }
    }
  };

  const getCurrentChartData = () => {
    switch (chartType) {
      case 'brand': return getTopBrandsData();
      case 'status': return statistics.statusStats;
      case 'warehouse': return statistics.warehouseStats;
      default: return getTopBrandsData(); // 默认显示品牌数据
    }
  };

  // 处理品牌数据：显示前10个品牌 + "其他"
  const getTopBrandsData = () => {
    const sortedBrands = [...statistics.brandStats].sort((a, b) => b.value - a.value);
    const top10Brands = sortedBrands.slice(0, 10);
    const otherBrands = sortedBrands.slice(10);

    // 计算"其他"品牌的数据
    const otherValue = otherBrands.reduce((sum, brand) => sum + brand.value, 0);
    const otherProducts = otherBrands.reduce((sum, brand) => sum + brand.products, 0);

    const result = [...top10Brands];
    if (otherBrands.length > 0 && otherValue > 0) {
      result.push({
        name: '其他',
        value: otherValue,
        products: otherProducts,
        inventory: otherBrands.reduce((sum, brand) => sum + ((brand as any).inventory || 0), 0)
      });
    }

    return result;
  };

  // 专门用于柱状图的品牌数据（按价值排序的前10个 + 其他）
  const getBarChartData = () => {
    return getTopBrandsData().slice(0, 11); // 确保最多显示11个（10个品牌 + 其他）
  };

  const colors = ['#8B5CF6', '#06B6D4', '#10B981', '#F59E0B', '#EF4444', '#EC4899', '#84CC16', '#6366F1', '#F97316', '#A855F7', '#06D6A0'];

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('zh-TW', {
      style: 'currency',
      currency: 'TWD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  // 自定义 Tooltip 组件，悬停时显示白色文字
  const CustomTooltip = ({ active, payload, label }: TooltipProps<any, any>) => {
    if (active && payload && payload.length) {
      return (
        <div
          style={{
            backgroundColor: '#1e293b',
            border: '1px solid #475569',
            borderRadius: '8px',
            padding: '8px 12px',
          }}
        >
          <p style={{ color: '#ffffff', margin: 0, fontWeight: 500 }}>
            {`${label}`}
          </p>
          <p style={{ color: '#ffffff', margin: '4px 0 0 0' }}>
            {`${chartType === 'brand' ? '產品款式' : '產品數量'}: ${payload[0].value}`}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="outline"
            onClick={() => {
              localStorage.setItem('selectedCategory', ANALYTICS_CATEGORY);
              navigate(ROUTES.dashboard.root);
            }}
            className="bg-card/50 border-border text-muted-foreground hover:bg-accent/50"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {language === 'zh' ? '返回綜合統計' : 'Back to Analytics'}
          </Button>
          <div className="flex items-center gap-2">
            <TrendingUp className="w-6 h-6 text-purple-400" />
            <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">產品統計</h1>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card className="bg-card/80 border-border">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-muted-foreground text-sm">總產品數</p>
                  <p className="text-3xl font-bold text-foreground">{statistics.totalProducts.toLocaleString()}</p>
                </div>
                <BarChart3 className="w-8 h-8 text-purple-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card/80 border-border">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-muted-foreground text-sm">總庫存價值</p>
                  <p className="text-3xl font-bold text-green-400">{formatCurrency(statistics.totalValue)}</p>
                </div>
                <TrendingUp className="w-8 h-8 text-green-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card/80 border-border">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-muted-foreground text-sm">總庫存數量</p>
                  <p className="text-3xl font-bold text-cyan-400">{statistics.totalInventory.toLocaleString()}</p>
                </div>
                <PieChart className="w-8 h-8 text-cyan-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Chart Controls */}
          <Card className="bg-card/80 border-border">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-foreground">品牌統計圖表</CardTitle>
                <Select value={chartType} onValueChange={(value: any) => setChartType(value)}>
                  <SelectTrigger className="w-40 bg-background/50 border-input text-foreground">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-popover border-border">
                    <SelectItem value="brand" className="text-foreground">按品牌</SelectItem>
                    <SelectItem value="status" className="text-foreground">按狀態</SelectItem>
                    <SelectItem value="warehouse" className="text-foreground">按倉庫</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-400 mx-auto"></div>
                  <p className="text-muted-foreground mt-2">載入中...</p>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <RechartsPieChart>
                    <Pie
                      data={chartType === 'brand' ? getTopBrandsData() : getCurrentChartData()}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={120}
                      paddingAngle={5}
                      dataKey={chartType === 'brand' ? 'products' : 'products'}
                    >
                      {(chartType === 'brand' ? getTopBrandsData() : getCurrentChartData()).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                  </RechartsPieChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          {/* Bar Chart */}
          <Card className="bg-card/80 border-border">
            <CardHeader>
              <CardTitle className="text-foreground">價值分布圖（按品牌）</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-400 mx-auto"></div>
                  <p className="text-muted-foreground mt-2">載入中...</p>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={getBarChartData()}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#475569" />
                    <XAxis
                      dataKey="name"
                      stroke="#94a3b8"
                      angle={-45}
                      textAnchor="end"
                      height={60}
                    />
                    <YAxis stroke="#94a3b8" />
                    <Tooltip
                      formatter={(value, name) => [formatCurrency(Number(value)), '總價值']}
                      contentStyle={{
                        backgroundColor: '#1e293b',
                        border: '1px solid #475569',
                        borderRadius: '8px',
                        color: '#fff'
                      }}
                    />
                    <Bar dataKey="value" fill="#8B5CF6" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Detailed Statistics Table */}
        <Card className="bg-slate-800/80 border-slate-700 mt-6">
          <CardHeader>
            <CardTitle className="text-foreground">詳細統計數據</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left text-muted-foreground p-3">品牌</th>
                    <th className="text-right text-muted-foreground p-3">產品款式</th>
                    <th className="text-right text-muted-foreground p-3">產品數量</th>
                    <th className="text-right text-muted-foreground p-3">總價值</th>
                  </tr>
                </thead>
                <tbody>
                  {getCurrentChartData()
                    .sort((a, b) => b.value - a.value) // 按總價值降序排序
                    .map((item, index) => (
                    <tr key={index} className="border-b border-border hover:bg-accent/30">
                      <td className="text-foreground p-3">{item.name}</td>
                      <td className="text-muted-foreground p-3 text-right">{item.products.toLocaleString()}</td>
                      <td className="text-muted-foreground p-3 text-right">
                        {(item as any).inventory?.toLocaleString() || '0'}
                      </td>
                      <td className="text-muted-foreground p-3 text-right">{formatCurrency(item.value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ProductStatistics;
